import React, { useContext } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import WhatSaunaForm from './WhatSaunaForm';
import { KontentStringValue } from '../../../types/KontentProps';
import { useProducts } from '../Global/DataUtils/productQueries';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';
import { normalizeLocale } from '../../utils/locale';
import { pickLocalizedEdges } from '../../utils/localizedNodes';
interface WellnessType {
    codename: string;
    name: string;
    img: string | { url: string; width: number; height: number };
}

interface WellnessQueryResults {
    allKontentItemMediaWellnessImage: {
        edges: {
            node: {
                preferred_language?: string;
                elements: {
                    image: {
                        value: {
                            url: string;
                            description?: string;
                            name?: string;
                            width?: number;
                            height?: number;
                        }[];
                    };
                    wellness_type: {
                        value: {
                            codename: string;
                        }[];
                    };
                };
            };
        }[];
    };
}

interface CapacityTag {
    value: {
        codename: string;
        name: string;
    }[];
}

export interface WhatSaunaProps {
    kontentItemId?: string;
    headline: KontentStringValue;
    description_copy: KontentStringValue;
    interested_in_subheadline: KontentStringValue;
    anchor_name: KontentStringValue;
    contact_form: KontentStringValue;
    capacity_need_subheadline: KontentStringValue;
    cta_label: KontentStringValue;
    results_headline: KontentStringValue;
    wellness_type: {
        value: WellnessType[];
    };
    wellness_type_for_german_site: {
        value: WellnessType[];
    };
    wellness_type_for_french_site: {
        value: WellnessType[];
    };
    capacity_tag: CapacityTag;
    capacity_tag_for_german_site: CapacityTag;
    capacity_tag_for_french_site: CapacityTag;
}

const WhatSaunaContainer: React.FC<WhatSaunaProps> = ({ ...props }) => {
    let { wellness_type, ...rest } = props;
    const pageMetadata = useContext(PageMetadataContext);
    const allProducts = useProducts().filter(
        (product) =>
            normalizeLocale(product.preferred_language) ==
            normalizeLocale(pageMetadata.preferredLanguage)
    );
    ///add wellnesstypeGe here in querry

    const wellnessImagesQueryResults = useStaticQuery(graphql`
        query WellnessImageQuery {
            allKontentItemMediaWellnessImage {
                edges {
                    node {
                        preferred_language
                        elements {
                            image {
                                value {
                                    url
                                    description
                                    name
                                    width
                                    height
                                }
                            }
                            wellness_type {
                                value {
                                    codename
                                }
                            }
                            wellness_type___de_de {
                                value {
                                    codename
                                }
                            }
                            wellness_type___fr_fr {
                                value {
                                    codename
                                }
                            }
                        }
                    }
                }
            }
        }
    `);

    wellness_type = addWellnessImages(
        wellness_type?.value,
        wellnessImagesQueryResults,
        pageMetadata?.preferredLanguage
    );

    return (
        <WhatSaunaForm
            {...rest}
            wellness_type={wellness_type}
            allProducts={allProducts}
        />
    );
};

export default WhatSaunaContainer;

function addWellnessImages(
    wellnessTypeValues: WellnessType[],
    wellnessImageQueryResults: WellnessQueryResults,
    preferredLanguage?: string
) {
    const wellnessImages = pickLocalizedEdges(
        wellnessImageQueryResults?.allKontentItemMediaWellnessImage?.edges,
        preferredLanguage
    ).map((result) => {
        const asset = result?.node?.elements?.image?.value[0];
        return {
            img: asset?.url
                ? {
                      url: asset.url,
                      width: asset.width || 152,
                      height: asset.height || 152,
                  }
                : '',
            codename: result?.node?.elements?.wellness_type?.value[0]?.codename,
        };
    });

    const wellnessTypeWithImages = wellnessTypeValues?.map((wt) => {
        const wellnessTypeImage = wellnessImages.find((wellnessImage) => {
            return wt.codename === wellnessImage.codename;
        });
        wt.img = wellnessTypeImage?.img || '';

        return wt;
    });

    return { value: wellnessTypeWithImages };
}

export const fragmentWhatSaunaIsRightForYouForm = graphql`
    fragment WhatSaunaIsRightForYouForm on kontent_item_component___what_sauna_is_right_for_you {
        elements {
            headline {
                value
            }
            description_copy {
                ...kontentRichText
            }
            interested_in_subheadline {
                value
            }
            cta_label {
                value
            }
            anchor_name {
                value
            }
            contact_form {
                value
            }
            capacity_need_subheadline {
                value
            }
            capacity_tag {
                value {
                    codename
                    name
                }
            }
            capacity_tag_for_german_site {
                value {
                    codename
                    name
                }
            }
            capacity_tag_for_french_site {
                value {
                    codename
                    name
                }
            }
            wellness_type {
                value {
                    codename
                    name
                }
            }
            wellness_type_for_german_site {
                value {
                    codename
                    name
                }
            }
            wellness_type_for_french_site {
                value {
                    codename
                    name
                }
            }
            background_image {
                ...kontentImage
            }
            results_headline {
                value
            }
            cta_configuration__cta_label {
                value
            }
            cta_configuration__cta_location {
                ...kontentCtaLocation
            }
            cta_configuration__cta_location_anchor_point {
                value
            }
            cta_configuration__cta_window_action {
                value {
                    codename
                }
            }
            show_2nd_cta {
                value {
                    codename
                }
            }
        }
    }
`;
